import { useLayoutEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import {
  isPromptVersionLogsSearchContextValid,
  migratePromptVersionLogsSearchContextParams,
  parsePromptVersionLogsSearchContext,
  resetPromptVersionLogsSearchContextParams,
} from "../utils/promptVersionLogsSearchContext";

/** Validates URL search context for the current prompt version; migrates or resets as needed. */
export const useEnsurePromptVersionLogsSearchContext = (
  promptId: string | undefined,
  versionId: string | undefined,
): boolean => {
  const [searchParams, setSearchParams] = useSearchParams();
  const context = useMemo(() => {
    if (!promptId || !versionId) {
      return null;
    }
    const parsed = {
      promptId: Number(promptId),
      promptVersionNumber: Number(versionId),
    };
    if (!Number.isFinite(parsed.promptId) || !Number.isFinite(parsed.promptVersionNumber)) {
      return null;
    }
    return parsed;
  }, [promptId, versionId]);

  const isReady = !context || isPromptVersionLogsSearchContextValid(searchParams, context);

  useLayoutEffect(() => {
    if (!context || isReady) {
      return;
    }

    const stored = parsePromptVersionLogsSearchContext(searchParams);
    // Missing context: remap legacy globals into sidebar (if needed) and stamp searchContext.
    // Mismatched context (switched prompt/version): clear scoped Analytics state.
    const nextParams = stored
      ? resetPromptVersionLogsSearchContextParams(searchParams, context)
      : migratePromptVersionLogsSearchContextParams(searchParams, context);

    setSearchParams(nextParams, { replace: true });
  }, [context, isReady, searchParams, setSearchParams]);

  return isReady;
};
